/** @format */

import {useState} from 'react';

import {Box} from '@mui/material';
import {CreditCard, CurrencyRuble} from '@mui/icons-material';
import {Trans, useTranslation} from 'react-i18next';

import {providerNewIconSrc} from 'helpers/imageHelpers';
import {BillingDiscountPeriod} from 'graphql/generated/types';
import {divideBySpaces} from 'helpers/numberHelpers';
import {AvailableBusinessTariff} from 'features/tariffs/types';
import {getActualMonthlyAmount} from 'pages/Tariffs/helpers/getActualMonthlyAmount';
import {getPriceWithoutDiscount} from 'pages/Tariffs/helpers/getPriceWithoutDiscount';
import {getBudgetDescription} from 'pages/Tariffs/helpers/getBudgetDescription';
import {getMinBudgetDescription} from 'pages/Tariffs/helpers/getMinBudgetDescription';
import {CardData} from 'pages/Tariffs/types';

import {
  ActualPriceNoteText,
  ActualPriceText,
  AskPriceBox,
  CurrencyWrap,
  DescriptionBox,
  DescriptionText,
  OldPriceDummy,
  OldPriceText,
  PlanBox,
  PriceBox,
  PriceNotesBox,
  Root,
  SelectButton,
} from './styles';
import {TariffHeaders} from './components/TariffHeaders';
import {CabinetHelpContent} from './components/CabinetHelpContent';
import {BudgetHelpContent} from './components/BudgetHelpContent';
import {PlanElement} from './components/PlanElement';
import {ID_BUTTON, ID_OLD_PRICE, ID_ROOT} from './constants';

export interface CardProps {
  duration: BillingDiscountPeriod;
  cardData: CardData;
  onSelect: (id: AvailableBusinessTariff, isPrice: boolean) => void;
  loading: boolean;
  selectedTariffId: AvailableBusinessTariff | null;
}

const Card = ({onSelect, duration, loading, selectedTariffId, cardData}: CardProps) => {
  const [hover, setHover] = useState<boolean>(false);

  const {t} = useTranslation();

  const {description, maxSubAccounts, spentMin, spentMax, isUltra, prices, id} = cardData;

  const cabinets = isUltra
    ? t('plurals.cabinetsFrom', {count: maxSubAccounts})
    : t('plurals.cabinets', {count: maxSubAccounts});

  const budget = isUltra
    ? getMinBudgetDescription(spentMax, t)
    : getBudgetDescription(spentMin, spentMax, t);

  const price = prices.find(price => price.period === duration);
  const isPrice = typeof price?.amount === 'number';

  const yandexIcon = providerNewIconSrc('YANDEX', 40);

  // Цена первого счёта со всеми скидками; без партнёрской равна прайсовой
  const actualAmount = getActualMonthlyAmount({
    amount: price?.amount,
    firstPaymentMonthlyAmount: price?.firstPaymentMonthlyAmount,
  });

  const priceWithoutDiscount = isPrice
    ? getPriceWithoutDiscount({
        amount: price.amount,
        discount: price.discount,
      })
    : 0;

  const showOldPrice = priceWithoutDiscount !== actualAmount;

  const handleSelect = () => {
    onSelect(id, isPrice);
  };

  return (
    <Root
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={ID_ROOT}
    >
      <TariffHeaders id={id} />
      <DescriptionBox>
        <DescriptionText>{description}</DescriptionText>
      </DescriptionBox>

      <PlanBox>
        <PlanElement
          IconComponent={<img src={yandexIcon} alt='yandex' width='24' height='24' />}
          upperText={cabinets}
          lowerText={t('Яндекс Директ')}
          helpHeaderText={t('Рекламные кабинеты в AdSensor')}
          helpContent={CabinetHelpContent}
        />

        <PlanElement
          IconComponent={
            <CurrencyWrap>
              <CurrencyRuble fontSize='small' />
            </CurrencyWrap>
          }
          upperText={t('Рекламный бюджет')}
          lowerText={budget}
          helpHeaderText={t('Ограничение рекламного бюджета')}
          helpContent={BudgetHelpContent}
        />
      </PlanBox>

      {isPrice ? (
        <PriceBox>
          {showOldPrice ? (
            <OldPriceText data-testid={ID_OLD_PRICE}>
              {divideBySpaces(priceWithoutDiscount)} ₽
            </OldPriceText>
          ) : (
            <OldPriceDummy />
          )}
          <PriceNotesBox>
            <ActualPriceText>{divideBySpaces(actualAmount)}</ActualPriceText>
            <Box>
              <ActualPriceNoteText>₽</ActualPriceNoteText>
              <ActualPriceNoteText>
                <Trans>в месяц</Trans>
              </ActualPriceNoteText>
            </Box>
          </PriceNotesBox>
        </PriceBox>
      ) : (
        <AskPriceBox>
          <ActualPriceNoteText>
            <Trans>Цена по запросу</Trans>
          </ActualPriceNoteText>
        </AskPriceBox>
      )}

      <SelectButton
        onClick={handleSelect}
        disabled={loading}
        active={selectedTariffId === id}
        cardHover={hover}
        data-testid={ID_BUTTON}
      >
        {isPrice ? (
          <>
            <CreditCard fontSize='small' />
            <Trans>Приобрести</Trans>
          </>
        ) : (
          <Trans>Оставить заявку</Trans>
        )}
      </SelectButton>
    </Root>
  );
};

export {Card};
